import { InvoiceStatus } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceFilters";
import { subDays } from "date-fns";

const OUTSTANDING_STATUSES: readonly InvoiceStatus[] = [
  InvoiceStatus.Draft,
  InvoiceStatus.Pending,
  InvoiceStatus.Unpaid,
  InvoiceStatus.PartiallyPaid,
];

export const OLDER_CUTOFF_DAYS = 90;

export const isOutstanding = (status: InvoiceStatus): boolean =>
  OUTSTANDING_STATUSES.includes(status);

// Helpers are deliberately structural — they only need invoiceStatus / created
// off of `invoiceSummary` and stay decoupled from the wider Invoice shape so
// they work against both the v2 card shape and any future variant.
type HasInvoiceStatus = { invoiceSummary: { invoiceStatus: InvoiceStatus } };
type HasCreated = { invoiceSummary: { created: string } };

export const splitOutstandingPrevious = <T extends HasInvoiceStatus>(
  invoices: readonly T[],
): { outstanding: T[]; previous: T[] } => {
  const outstanding: T[] = [];
  const previous: T[] = [];
  for (const invoice of invoices) {
    if (isOutstanding(invoice.invoiceSummary.invoiceStatus)) {
      outstanding.push(invoice);
    } else {
      previous.push(invoice);
    }
  }
  return { outstanding, previous };
};

export const partitionByOlderCutoff = <T extends HasCreated>(
  invoices: readonly T[],
  now: Date,
  cutoffDays: number = OLDER_CUTOFF_DAYS,
): { recent: T[]; older: T[] } => {
  const cutoff = subDays(now, cutoffDays);
  const recent: T[] = [];
  const older: T[] = [];
  for (const invoice of invoices) {
    const created = new Date(invoice.invoiceSummary.created);
    if (created >= cutoff) {
      recent.push(invoice);
    } else {
      older.push(invoice);
    }
  }
  return { recent, older };
};
